import addChangeItemOptionMasterDialogTemplate from './AddChangeItemOptionMasterDialog.template.html';
import optionMasterValueInputManagerTemplate from './optionMasterValueInputManager.template.html';

/*******************************************************************************
 * Service: AddChangeItemOptionMasterDialog
 *
 * This allows for the management of an option definition and any values it may
 * have, if applicable. The text input option will not have any values associated
 * as it is user input, while the selection list will display all associated
 * values.
 *
 * Note: The required flag will be indication that the user input is required.
 * Such as the text being present or a selection is chosen.
 *******************************************************************************/
/* @ngInject */ export function AddChangeItemOptionMasterDialogService($uibModal, $q, Alert, BooksSvc, SERVER, BusyScreen) {
  /** Dialog Controller. */
  var AddChangeItemOptionMasterDialogController = function (
    $scope,
    BusyScreen,
    excludeGuest,
    optionMaster,
    KEYCODES
  ) {
    /** Object to store data and init valueInput */ $scope.data = {
      valueInput: null
    };
    /** Places optionMaster on the scope. */ $scope.optionMaster =
      optionMaster;
    /** Indication of new option  */ $scope.isNew = function () {
      return !$scope.optionMaster.id;
    };
    /** CSMValidators validate fields */ $scope.validators = {};

    /** Cache optionMaster type. **/
    var optionMasterType = !$scope.isNew() ? $scope.optionMaster.type : 0;

    /*-----------------------------------------------------*/
    /* When modifying an existing text input type, prevent */
    /* the selection list from being shown in the list.    */
    /*-----------------------------------------------------*/
    var excludeSelectionType =
      optionMasterType == SERVER.BooksDBConstants.MOT_TEXT_INPUT;

    /** Selection types **/
    $scope.types = [
      {
        valueType: SERVER.BooksDBConstants.MOT_SELECTION_LIST,
        typeLabel: 'Selection List',
        useValues: true,
        allowChange: false,
        exclude: excludeSelectionType
      },
      {
        valueType: SERVER.BooksDBConstants.MOT_TEXT_INPUT,
        typeLabel: 'Text Input',
        useValues: false,
        allowChange: true,
        exclude: false
      },
      {
        valueType: SERVER.BooksDBConstants.MOT_GUEST_NAME,
        typeLabel: 'Guest Name',
        useValues: false,
        allowChange: false,
        exclude: excludeGuest
      }
    ];

    /**************************************************************************
     * addValue */
    /**
     * Add value to array and reset focus on input field.
     **************************************************************************/
    $scope.addValue = function () {
      /*-------------------------*/
      /* Exit if nothing to add. */
      /*-------------------------*/
      if (!$scope.data.valueInput || !$scope.selectedType().useValues)
        return;

      /*---------------------------------------------------*/
      /* Create array if needed and add value. After       */
      /* adding, clear input field and return focus to it. */
      /*---------------------------------------------------*/
      if (!$scope.optionMaster.values) $scope.optionMaster.values = [];

      $scope.optionMaster.values.push($scope.data.valueInput);

      $scope.data.valueInput = '';
      document.getElementById('addNewOptionValueID').focus();
    };

    /*************************************************************************
     * allowTypeChange */
    /**
     * Evaluates the type for the ability to change.
     *
     * @param   optionMasterType  value type to check if existing option
     *                            type allows change.
     * @return  Boolean           false: disables drop down
     *                            true: enables drop down.
     *************************************************************************/
    $scope.allowTypeChange = function () {
      /*---------------------------------------*/
      /* Allow type changing if option is new. */
      /*---------------------------------------*/
      if ($scope.isNew()) return true;

      /*------------------------------------------*/
      /* Return types.allowChange value for type. */
      /*------------------------------------------*/
      return ($scope.types || []).some(function (type) {
        /*-----------------------------------------------------------------*/
        /* Return true/false dependent on type that allows changes or not. */
        /*-----------------------------------------------------------------*/
        if (type.valueType == optionMasterType) return type.allowChange;
      });
    };

    /*************************************************************************
     * checkValue */
    /**
     * Display error if present, uses csmValidators.
     *
     * @return  String  Error alert to display. Null=Valid
     *************************************************************************/
    $scope.checkValue = function () {
      if (
        $scope.selectedType() &&
        $scope.selectedType().useValues &&
        (!$scope.optionMaster.values ||
          !$scope.optionMaster.values.length)
      )
        return 'At least one value is required.';

      return null;
    };

    /*************************************************************************
     * finish */
    /**
     * Finish button handler. If data is valid, save and close the modal
     * dialog and return the new/modified option.
     *************************************************************************/
    $scope.finish = function () {
      if (!$scope.validators.validate()) return;

      BusyScreen.setBusy(true);
      BooksSvc.saveItemOptionMaster($scope.optionMaster)
        .then(
          function (resp) {
            $scope.optionMaster.id = Number(resp.data);
            $scope.$close($scope.optionMaster);
          },
          /*----------------------*/
          /* Save Error Handling. */
          /*----------------------*/
          function (resp) {
            Alert.error(
              resp.parsedResponse
                ? resp.parsedResponse.message()
                : resp.statusText,
              'Cannot Save Option'
            );
          }
        )
        .finally(function () {
          BusyScreen.setBusy(false);
        });
    };

    /*************************************************************************
     * getTitle */
    /**
     * Returns text to display for the modal title.
     *
     * @return  String  Display correct title for dialog.
     *************************************************************************/
    $scope.getTitle = function () {
      return $scope.isNew() ? 'Add New Option' : 'Update Option';
    };

    /*************************************************************************
     * hideValue */
    /**
     * Hide inputs when the selectedType doesn't use values or when no type
     * has been selected.
     *
     * @return  Boolean  Boolean indication to hide or show.
     *************************************************************************/
    $scope.hideValue = function () {
      return !$scope.selectedType() || !$scope.selectedType().useValues;
    };

    /*************************************************************************
     * selectedType */
    /**
     * Getter/setter for the option type.
     *
     * @param  value   Selected type.
     *************************************************************************/
    $scope.selectedType = function (value) {
      /*----------------------------------------------------*/
      /* Getter - Return corresponding type for option.type */
      /*----------------------------------------------------*/
      if (!arguments.length)
        return $scope.types
          .filter(function (t) {
            return t.valueType == $scope.optionMaster.type;
          })
          .shift();

      /*------------------------------------------------------------------*/
      /* Setter - Update optionMaster.type and clear values if not in use */
      /*------------------------------------------------------------------*/
      $scope.optionMaster.type = value ? value.valueType : 0;

      if (value && $scope.selectedType().useValues == true)
        $scope.optionMaster.values = [];

      if (value && $scope.selectedType().useValues == false)
        delete $scope.optionMaster.values;
    };
  };

  /*************************************************************************
   * show */
  /**
   * Show the modal dialog for reviewing and managing Item Options
   * This will be used to input new options or change existing options.
   *
   * @param  optionID      Option ID to update. 0 or Undefined = new.
   * @param  excludeGuest  Flag to exclude the guest name from the list of
   *                       display types. The option is included by default.
   *************************************************************************/
  this.show = function (optionID, excludeGuest) {
    BusyScreen.setBusy(true);
    /*----------------------------------------------------------------*/
    /* When itemOption has been provided, attempt to load the         */
    /* itemOptionName, otherwise a new option object is to be managed */
    /*----------------------------------------------------------------*/
    return $q
      .when(
        optionID
          ? BooksSvc.getItemOptionMaster(optionID).then(function (resp) {
              return resp.data;
            })
          : {}
      )
      .then(function (optionMaster) {
        BusyScreen.setBusy(false);

        /*------------------------------------------*/
        /* Alert when Item Option name is not found */
        /*------------------------------------------*/
        if (!optionMaster) {
          Alert.info('Option not found', 'Option Missing');
          return $q.reject();
        }

        /*------------------------------------------*/
        /* When new or existing option, open modal. */
        /*------------------------------------------*/
        return $uibModal.open({
          template: addChangeItemOptionMasterDialogTemplate,
          size: 'md',
          backdrop: 'static',
          controller: AddChangeItemOptionMasterDialogController,
          resolve: {
            excludeGuest: function () {
              return !!excludeGuest;
            },
            optionMaster: function () {
              return optionMaster;
            }
          }
        }).result;
      });
  };
}

/* @ngInject */ function OptionMasterValueInputManagerController() {
  /** Reference to this component */ var mSelf = this;
  /** Cached Value before editing */ var mPrevious = null;

  /**************************************************************************
   * onFocus */
  /***
   * Store value on focus.
   **************************************************************************/
  mSelf.onFocus = function () {
    mPrevious = mSelf.optionMaster.values[mSelf.index];
  };

  /**************************************************************************
   * onBlur */
  /***
   * Checks presence of data in value, where value is not present reverts to
   * stored value onFocus(mPrevious).
   **************************************************************************/
  mSelf.onBlur = function () {
    if (!mSelf.optionMaster.values[mSelf.index])
      mSelf.optionMaster.values[mSelf.index] = mPrevious;
  };

  /**************************************************************************
   * removeValue */
  /***
   * Removes values by index from optionMaster.values.
   *
   * @param  index  Deletes value by index.
   **************************************************************************/
  mSelf.removeValue = function (index) {
    mSelf.optionMaster.values.splice(index, 1);
  };
}

/******************************************************************************
 * optionMasterValueInputManager */
/**
 * Manages input fields for item option types that use values. Stores value on
 * focus, on blur if value changed to empty/null revert to stored value.
 *
 * @binding  optionMaster  ItemOptionMaster parent object
 * @binding  index         index in optionMaster.values array
 ******************************************************************************/
export const optionMasterValueInputManagerComponent = {
  template: optionMasterValueInputManagerTemplate,
  bindings: { optionMaster: '<', index: '<' },
  controllerAs: 'viewModel',
  controller: OptionMasterValueInputManagerController
}
